/*
 * Wishlist Module
 * 
 * A lightweight, localStorage-based wishlist for Shopify themes.
 * Works with Shopify's CDN caching since state is managed client-side.
 * 
 * Features:
 * - Auto-injects header wishlist icon (no theme edit required)
 * - Auto-injects wishlist button on product pages (no theme edit required)
 * - Uses <wishlist-button> custom element
 * 
 * Events dispatched on document:
 * - wishlist:added   { detail: { handle, product } }
 * - wishlist:removed { detail: { handle } }
 * - wishlist:changed { detail: { items } }
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'shopify_wishlist';
  const WISHLIST_PAGE = '/pages/onskeliste';

  // SVG Icons (inline to avoid dependency on Liquid)
  const ICONS = {
    heartOutline: '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-heart" viewBox="0 0 20 20"><path fill="currentColor" fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/></svg>',
    heartFilled: '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-heart-filled" viewBox="0 0 20 20"><path fill="currentColor" d="M10 17.495l-7.696-7.594a5.29 5.29 0 0 1 0-7.549c2.113-2.084 5.538-2.084 7.65 0l.046.045.046-.045c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593z"/></svg>'
  };

  // Wishlist data management
  const Wishlist = {
    getItems() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        console.warn('Wishlist: Could not parse localStorage', e);
        return [];
      }
    },

    saveItems(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { items } }));
        this.updateAllButtons();
        this.updateCount();
      } catch (e) {
        console.warn('Wishlist: Could not save to localStorage', e);
      }
    },

    hasItem(handle) {
      return this.getItems().some(item => item.handle === handle);
    },

    addItem(handle, productData = {}) {
      if (this.hasItem(handle)) return false;
      
      const items = this.getItems();
      const newItem = {
        handle,
        addedAt: Date.now(),
        ...productData
      };
      items.push(newItem);
      this.saveItems(items);
      
      document.dispatchEvent(new CustomEvent('wishlist:added', { 
        detail: { handle, product: newItem } 
      }));
      return true;
    },

    removeItem(handle) {
      const items = this.getItems();
      const filtered = items.filter(item => item.handle !== handle);
      
      if (filtered.length !== items.length) {
        this.saveItems(filtered);
        document.dispatchEvent(new CustomEvent('wishlist:removed', { detail: { handle } }));
        return true;
      }
      return false;
    },

    toggleItem(handle, productData = {}) {
      if (this.hasItem(handle)) {
        this.removeItem(handle);
        return false;
      } else {
        this.addItem(handle, productData);
        return true;
      }
    },

    clearAll() {
      this.saveItems([]);
    },

    getCount() {
      return this.getItems().length;
    },

    // Update all wishlist buttons on the page
    updateAllButtons() {
      document.querySelectorAll('[data-wishlist-handle]').forEach(el => {
        const handle = el.dataset.wishlistHandle;
        const isInWishlist = this.hasItem(handle);
        const button = el.querySelector('button') || el;
        
        button.classList.toggle('is-wishlisted', isInWishlist);
        button.setAttribute('aria-pressed', isInWishlist);
        
        // Update aria-label
        const addText = el.dataset.wishlistAddText || 'Add to wishlist';
        const removeText = el.dataset.wishlistRemoveText || 'Remove from wishlist';
        button.setAttribute('aria-label', isInWishlist ? removeText : addText);
      });
    },

    // Update wishlist count badges and header icon visibility
    updateCount() {
      const count = this.getCount();
      document.querySelectorAll('[data-wishlist-count]').forEach(el => {
        el.textContent = count;
      });
      // Hide/show the header wishlist icon based on count
      document.querySelectorAll('[data-wishlist-header-icon]').forEach(el => {
        el.classList.toggle('hidden', count === 0);
      });
    }
  };

  // ================================
  // Auto-inject Header Wishlist Icon
  // ================================
  function injectHeaderWishlistIcon() {
    // Skip if already injected
    if (document.querySelector('[data-wishlist-header-icon]')) return;

    // Find the cart icon in the header to insert before it
    const cartIcon = document.querySelector('header .header__icon--cart, header #cart-icon-bubble, header a[href*="/cart"]');
    if (!cartIcon) return;

    const wishlistLink = document.createElement('a');
    wishlistLink.href = WISHLIST_PAGE;
    wishlistLink.className = 'header__icon header__icon--wishlist link focus-inset hidden';
    wishlistLink.setAttribute('aria-label', 'Wishlist');
    wishlistLink.setAttribute('data-wishlist-header-icon', '');
    wishlistLink.innerHTML = `
      <span class="svg-wrapper">${ICONS.heartOutline}</span>
      <div class="wishlist-count-bubble" data-wishlist-count-bubble>
        <span data-wishlist-count aria-hidden="true">0</span>
        <span class="visually-hidden">items in wishlist</span>
      </div>
    `;

    cartIcon.parentNode.insertBefore(wishlistLink, cartIcon);
  }

  // ================================
  // Auto-inject Product Page Wishlist Button
  // ================================
  function injectProductPageWishlist() {
    // Only run on product pages
    if (!document.querySelector('.product') && !document.querySelector('[data-section-type="product"]')) return;
    
    // Skip if already injected
    if (document.querySelector('.product-page-wishlist')) return;

    // Find quantity input container to insert after
    const quantityInput = document.querySelector('.product-form__quantity quantity-input, .product-form quantity-input');
    if (!quantityInput) return;

    // Get product data from the page
    const productJson = document.querySelector('[data-product-json]');
    const productForm = document.querySelector('form[action*="/cart/add"]');
    
    let handle = '';
    let title = '';
    let url = '';
    let image = '';
    let price = '';

    // Try to get product data from JSON
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        handle = product.handle || '';
        title = product.title || '';
        url = product.url || window.location.pathname;
        image = product.featured_image || (product.images && product.images[0]) || '';
        price = product.price ? (product.price / 100).toFixed(2) : '';
      } catch (e) {
        console.warn('Wishlist: Could not parse product JSON', e);
      }
    }

    // Fallback: try to get handle from URL
    if (!handle) {
      const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (pathMatch) handle = pathMatch[1];
    }

    // Fallback: get title from page
    if (!title) {
      const titleEl = document.querySelector('.product__title h1, .product__title, h1.title');
      if (titleEl) title = titleEl.textContent.trim();
    }

    // Fallback: get image from product gallery
    if (!image) {
      const galleryImg = document.querySelector('.product__media img, .product-media-container img, .product__media-item img, [data-product-media-type="image"] img');
      if (galleryImg) {
        // Get the src and try to get a smaller version for the wishlist
        let imgSrc = galleryImg.src || galleryImg.dataset.src || '';
        // If it's a Shopify CDN image, resize it to 300px width
        if (imgSrc && imgSrc.includes('cdn.shopify.com')) {
          imgSrc = imgSrc.replace(/(_\d+x\d*|\d+x\d*)?(\.[a-z]+)(\?.*)?$/i, '_300x$2');
        }
        image = imgSrc;
      }
    }

    // Fallback: get price from page
    if (!price) {
      const priceEl = document.querySelector('.price-item--regular, .price__regular .price-item, .product__price .money');
      if (priceEl) price = priceEl.textContent.trim();
    }

    if (!handle) return; // Can't create button without handle

    // Create wishlist button container
    const wishlistContainer = document.createElement('div');
    wishlistContainer.className = 'product-page-wishlist';
    wishlistContainer.innerHTML = `
      <wishlist-button
        class="wishlist-button no-js-hidden"
        data-wishlist-handle="${handle}"
        data-product-title="${title.replace(/"/g, '&quot;')}"
        data-product-url="${url || window.location.pathname}"
        data-product-image="${image}"
        data-product-price="${price}"
        data-wishlist-add-text="Add to wishlist"
        data-wishlist-remove-text="Remove from wishlist"
      >
        <button
          type="button"
          class="wishlist-button__btn product-page-wishlist__btn"
          aria-pressed="false"
          aria-label="Add to wishlist"
        >
          <span class="svg-wrapper wishlist-icon-outline">${ICONS.heartOutline}</span>
          <span class="svg-wrapper wishlist-icon-filled">${ICONS.heartFilled}</span>
        </button>
      </wishlist-button>
    `;

    // Insert after quantity input
    quantityInput.parentNode.insertBefore(wishlistContainer, quantityInput.nextSibling);

    // Initialize the newly created wishlist button
    Wishlist.updateAllButtons();
  }

  // Custom element for wishlist buttons
  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', class extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('button') || this;
      }

      connectedCallback() {
        this.handle = this.dataset.wishlistHandle;
        if (!this.handle) return;

        // Set initial state
        this.updateState();

        // Add click handler
        this.button.addEventListener('click', this.handleClick.bind(this));
      }

      handleClick(e) {
        e.preventDefault();
        e.stopPropagation();

        const productData = {
          title: this.dataset.productTitle,
          image: this.dataset.productImage,
          price: this.dataset.productPrice,
          url: this.dataset.productUrl
        };

        const added = Wishlist.toggleItem(this.handle, productData);
        
        // Visual feedback animation
        this.button.classList.add('wishlist-clicked');
        setTimeout(() => this.button.classList.remove('wishlist-clicked'), 300);
      }

      updateState() {
        const isInWishlist = Wishlist.hasItem(this.handle);
        this.button.classList.toggle('is-wishlisted', isInWishlist);
        this.button.setAttribute('aria-pressed', isInWishlist);
      }
    });
  }

  // Initialize on DOM ready
  function init() {
    // Auto-inject elements first
    injectHeaderWishlistIcon();
    injectProductPageWishlist();
    
    // Then update state
    Wishlist.updateAllButtons();
    Wishlist.updateCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when Shopify sections are reloaded (theme editor, AJAX)
  document.addEventListener('shopify:section:load', init);

  // Expose to global scope for external use
  window.Wishlist = Wishlist;
})();
